import { useInfiniteQuery } from '@tanstack/react-query';

import { useProjectsApi } from 'api/data/projects/projects-api';
import type { ApiDecompositionNode } from 'api/data/projects/types';
import { resolve } from 'api/resolve';

import { PAGE_SIZE, PEEK_LIMIT } from '../constants';
import { buildTreePage } from '../utils/buildTreePage';
import { toDecompositionNode } from '../utils/toDecompositionNode';
import { treeQueryKeys } from '../utils/treeQueryKeys';
import { useDecompositionRelation } from './useDecompositionRelation';

export function useTreeRoots(projectId: string, templateNodeId: string, proposalId?: string) {
  const { getTreeRootNodes } = useProjectsApi();
  const { relationId, technicalName } = useDecompositionRelation(templateNodeId);

  return useInfiniteQuery({
    queryKey: treeQueryKeys.roots({ projectId, templateNodeId, proposalId }),
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const apiNodes: ApiDecompositionNode[] = await resolve(
        getTreeRootNodes(projectId, relationId, technicalName, PEEK_LIMIT, pageParam, proposalId)
      );
      return buildTreePage(
        apiNodes.map((n) => toDecompositionNode(n, null)),
        pageParam,
        PAGE_SIZE
      );
    },
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextOffset : undefined),
    enabled: !!projectId && !!templateNodeId && !!relationId && !!technicalName
  });
}
